import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ActionRequestRead,
  ActionStatus,
  MessageRead,
  MessageSender,
  QueryRequest,
  SessionRead,
  StreamEvent,
  StreamEventType,
  SystemResponse,
  Task,
  WorkflowPlan,
} from '../julia-agent-openapi/models';
import { ActionRequestsService, ChatService, MessageService, SessionService } from '../julia-agent-openapi/services';
import { environment } from '../../../../environments/environment';

import { Subscription } from 'rxjs';

// Extended interface to track event types for styling
interface ExtendedMessageRead extends MessageRead {
  event_type?: StreamEventType;
}

@Component({
  selector: 'app-julia-chat',
  templateUrl: './julia-chat.component.html',
  styleUrls: ['./julia-chat.component.scss'],
})
export class JuliaChatComponent implements OnInit {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  isChatOpen = false;
  newMessage = '';
  logoJulia = environment.juliaLogo;
  sessions: SessionRead[] = [];
  selectedSession: SessionRead | null = null;
  messages: ExtendedMessageRead[] = [];
  temporaryMessages: ExtendedMessageRead[] = []; // For transient events like PROGRESS, TASK_PROGRESS
  currentTasks: Task[] = []; // Workflow tasks from workflow_plan
  pendingActionRequests: ActionRequestRead[] = []; // Action requests awaiting user approval

  loadingSessions = true;
  loadingMessages = false;

  loadingAssistant = false;

  searchOpened = false;
  
  private currentSubscription?: Subscription;
  showPreviewModal = false;
  selectedActionRequestForPreview: ActionRequestRead | null = null;
 

  constructor(
    private juliaSessionService: SessionService,
    private juliaMessageService: MessageService,
    private juliaChatService: ChatService,
    private juliaActionRequestsService: ActionRequestsService
  ) { }

  ngOnInit() {
    this.loadSessions();
  }

  toggleChat() {
    this.isChatOpen = !this.isChatOpen;
  }

  loadSessions() {
    this.loadingSessions = true;
    this.juliaSessionService.getSessionsApiV1SessionGet().subscribe({
      next: (res) => {
        this.sessions = res;
        this.loadingSessions = false;
      },
      error: (err: HttpErrorResponse) => {
        console.error('Erreur sessions:', err);
        this.loadingSessions = false;
      },
    });
  }

  createNewSession(loadMessages =true): Promise<SessionRead> {
  return new Promise((resolve, reject) => {
    this.juliaSessionService.createSessionApiV1SessionPost().subscribe({
      next: (session) => {
        this.sessions.unshift(session);
        this.selectSession(session, loadMessages);
        resolve(session);
      },
      error: (err: HttpErrorResponse) => {
        console.error('Erreur création session:', err);
        reject(err);
      },
    });
  });
}

  toggleSessionDropdown() {
    this.searchOpened = !this.searchOpened;
  }

  selectSession(session: SessionRead , loadMessages = true) {
    this.selectedSession = session;
    this.searchOpened = false; // fermer le menu après sélection
    if (loadMessages) {
      this.loadMessages(session.id!);
    }
  }

  loadMessages(sessionId: string) {
    this.loadingMessages = true;
    this.juliaMessageService
      .getMessagesApiV1MessageSessionSessionIdGet({ session_id: sessionId })
      .subscribe({
        next: (msgs) => {
          this.messages = msgs;
          this.loadingMessages = false;
          this.scrollToBottom();
        },
        error: (err: HttpErrorResponse) => {
          console.error('Erreur chargement messages:', err);
          this.loadingMessages = false;
        },
      });
  }

  async sendMessage() {
    if (!this.newMessage.trim() ) return;
   if (!this.selectedSession) {
    try {
      const session = await this.createNewSession(false);
      this.selectedSession = session;
    } catch (err) {
      console.error('Session creation failed', err);
      return;
    }
  }
  
    if(!this.selectedSession) return;

    const userMessage: MessageRead = {
      id: uuidv4(),
      session_id: this.selectedSession.id!,
      content: this.newMessage,
      type: MessageSender.User,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      meta: null,
    };

    this.messages = [...this.messages, userMessage];
    
    const request: QueryRequest = {
      query: this.newMessage,
      session_id: this.selectedSession.id!,
    };

    this.newMessage = '';
    this.scrollToBottom();

    this.loadingAssistant = true;
    const mainMessageId = uuidv4();
    const progressIds: string[] = [];

    this.currentSubscription = this.juliaChatService.streamQueryApiV1ChatQueryStreamPost({ body: request })
      .subscribe({
        next: (event: StreamEvent) => {
          
          switch (event.event_type) {
            case StreamEventType.PROGRESS:
              {
                // Clear previous progress messages and add new one temporarily
                this.temporaryMessages = [{
                  id: uuidv4(),
                  session_id: this.selectedSession!.id!,
                  content: event.data.content ?? 'Processing...',
                  type: MessageSender.Assistant,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  meta: null,
                  event_type: StreamEventType.PROGRESS,
                }];
                break;
              }
            case StreamEventType.CHUNK:
              {
                // Clear temporary messages when actual content arrives
                this.temporaryMessages = [];

                const mainMessage = this.messages.find(msg => msg.id === mainMessageId);
                if (mainMessage) {
                  mainMessage.content= event.data.content ?? '';
                  mainMessage.updated_at = new Date().toISOString();
                  mainMessage.event_type = StreamEventType.CHUNK;
                  this.messages = [...this.messages];
                }else{
                  const assistantMsg: ExtendedMessageRead = {
                    id: mainMessageId,
                    session_id: this.selectedSession!.id!,
                    content: event.data.content ?? '(vide)',
                    type: MessageSender.Assistant,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    meta: null,
                    event_type: StreamEventType.CHUNK,
                  };
                  this.messages = [...this.messages, assistantMsg];
                }
                
                break;

              }
              
            case StreamEventType.FINAL_RESULT:
              {
                // Clear temporary messages when final result arrives
                this.temporaryMessages = [];
      
                const mainMessage = this.messages.find(msg => msg.id === mainMessageId);
                if (mainMessage) {
                  mainMessage.content= event.data.content ?? '';
                  mainMessage.updated_at = new Date().toISOString();
                  mainMessage.event_type = StreamEventType.FINAL_RESULT;
                  this.messages = [...this.messages];
                }else{
                  const assistantMsg: ExtendedMessageRead = {
                    id: mainMessageId,
                    session_id: this.selectedSession!.id!,
                    content: event.data.content ?? '(vide)',
                    type: MessageSender.Assistant,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    meta: null,
                    event_type: StreamEventType.FINAL_RESULT,
                  };
                  this.messages = [...this.messages, assistantMsg];
                }
                break;
              }
            case StreamEventType.TASKS:
              {
                
                // Extract workflow plan and merge tasks intelligently
                if (event.data.workflow_plan && event.data.workflow_plan.tasks) {
                  const newTasks = event.data.workflow_plan.tasks;
                  
                  // Create a map of existing tasks by ID for quick lookup
                  const existingTasksMap = new Map(
                    this.currentTasks.map(task => [task.id, task])
                  );
                  
                  // Merge: keep existing task state if task ID exists, otherwise add new task
                  this.currentTasks = newTasks.map(newTask => {
                    const existingTask = existingTasksMap.get(newTask.id);
                    
                    if (existingTask) {
                      // Keep the frontend-managed state (status, completed_at, result)
                      return {
                        ...newTask,
                        status: existingTask.status,
                        completed_at: existingTask.completed_at,
                        result: existingTask.result,
                      };
                    }
                    
                    // New task, use as-is
                    return newTask;
                  });
                  
                }
                break;
              }
            case StreamEventType.TASK_START:
              {
                // Update task status to in_progress
                if (event.data.task_id) {
                  const task = this.currentTasks.find(t => t.id === event.data.task_id);
                  if (task) {
                    task.status = 'in_progress';
                    this.currentTasks = [...this.currentTasks];
                  }
                }
                break;
              }
            case StreamEventType.TASK_PROGRESS:
              {
                // Clear previous progress and add new one temporarily
                this.temporaryMessages = [{
                  id: uuidv4(),
                  session_id: this.selectedSession!.id!,
                  content: event.data.task_step || event.data.content || 'Task in progress...',
                  type: MessageSender.Assistant,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  meta: null,
                  event_type: StreamEventType.TASK_PROGRESS,
                }];
                break;
              }
            case StreamEventType.TASK_OUTPUT:
              {
                // Update task with output/result
                if (event.data.task_id && event.data.task_output) {
                  const task = this.currentTasks.find(t => t.id === event.data.task_id);
                  if (task) {
                    task.result = event.data.task_output;
                    this.currentTasks = [...this.currentTasks];
                  }
                }
                break;
              }
            case StreamEventType.TASK_COMPLETE:
              {
                // Update task status to completed
                if (event.data.task_id) {
                  const task = this.currentTasks.find(t => t.id === event.data.task_id);
                  if (task) {
                    task.status = 'completed';
                    task.completed_at = new Date().toISOString();
                    this.currentTasks = [...this.currentTasks];
                  }
                }
                
                break;
              }
            case StreamEventType.TASK_ERROR:
              {
                // Update task status to failed
                if (event.data.task_id) {
                  const task = this.currentTasks.find(t => t.id === event.data.task_id);
                  if (task) {
                    task.status = 'failed';
                    this.currentTasks = [...this.currentTasks];
                  }
                }
                
                // const taskErrorMsg: ExtendedMessageRead = {
                //   id: uuidv4(),
                //   session_id: this.selectedSession!.id!,
                //   content: event.data.task_error || event.data.content || 'Task error',
                //   type: MessageSender.Assistant,
                //   created_at: new Date().toISOString(),
                //   updated_at: new Date().toISOString(),
                //   meta: null,
                //   event_type: StreamEventType.TASK_ERROR,
                // };
                // this.messages = [...this.messages, taskErrorMsg];
                break;
              }
            case StreamEventType.AGENT_SUMMARY:
              {
                const agentSummaryMsg: ExtendedMessageRead = {
                  id: uuidv4(),
                  session_id: this.selectedSession!.id!,
                  content: event.data.agent_summary || event.data.content || 'Agent summary',
                  type: MessageSender.Assistant,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  meta: null,
                  event_type: StreamEventType.AGENT_SUMMARY,
                };
                this.messages = [...this.messages, agentSummaryMsg];
                break;
              }
            case StreamEventType.COMPLETE:
              {
                // Clear all temporary messages on completion
                this.temporaryMessages = [];
                // Clear tasks after completion
                this.currentTasks = [];
                // Clear pending action requests
                this.pendingActionRequests = [];
                
                const streamSession = event.data.session;
                this.selectedSession = streamSession || this.selectedSession;
                
                this.sessions = this.sessions.map(sess => {
                  if(sess.id === this.selectedSession!.id){
                    return streamSession ||sess ; 
                  }
                  return sess;
                });
                this.selectedSession = streamSession || this.selectedSession;
                
                break;
              }

            case StreamEventType.AUTHORIZATION_REQUIRED:
              {
                console.log("Auth required event received:", event);
                if (event.data.action_request) {
                  // Store the action request for user approval
                  const actionRequest = event.data.action_request as ActionRequestRead;
                  this.pendingActionRequests.push(actionRequest);
                }
                break;
              }
                            
            default:
              break;
          }

          this.scrollToBottom();
         
        },
        error: (err) => {
          console.error('Stream error:', err);
          this.loadingAssistant = false;
          this.currentSubscription = undefined;
        },
        complete: () => {
          this.loadingAssistant = false;
          this.currentSubscription = undefined;
        }
      });
  }

  cancelRequest() {
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
      this.currentSubscription = undefined;
      this.loadingAssistant = false;
      this.temporaryMessages = [];
      this.currentTasks = [];
      this.pendingActionRequests = [];
    }
  }
   isSubscriptionActive(): boolean {
    return this.currentSubscription !== undefined;
  }

  private scrollToBottom() {
    setTimeout(() => {
      if (this.scrollContainer) {
        this.scrollContainer.nativeElement.scrollTop =
          this.scrollContainer.nativeElement.scrollHeight;
      }
    }, 100);
  }

  isMessageSentByUser(msg: ExtendedMessageRead) {
    return msg.type === 'user';
  }

  getMessageEventClass(msg: ExtendedMessageRead): string {
    if (!msg.event_type) return '';
    
    switch (msg.event_type) {
      case StreamEventType.PROGRESS:
        return 'event-progress';
      case StreamEventType.CHUNK:
        return 'event-chunk';
      case StreamEventType.FINAL_RESULT:
        return 'event-final-result';
      case StreamEventType.TASK_START:
        return 'event-task-start';
      case StreamEventType.TASK_PROGRESS:
        return 'event-task-progress';
      case StreamEventType.TASK_COMPLETE:
        return 'event-task-complete';
      case StreamEventType.TASK_ERROR:
        return 'event-task-error';
      case StreamEventType.AGENT_SUMMARY:
        return 'event-agent-summary';
      case StreamEventType.MESSAGE_START:
        return 'event-message-start';
      case StreamEventType.MESSAGE_DELTA:
        return 'event-message-delta';
      case StreamEventType.MESSAGE_END:
        return 'event-message-end';
      default:
        return '';
    }
  }

  getTaskStatusClass(task: Task): string {
    switch (task.status) {
      case 'completed':
        return 'completed';
      default:
        return '';
    }
  }

  approveActionRequest(actionRequest: ActionRequestRead) {
    this.juliaActionRequestsService
      .resolveActionRequestApiV1ActionRequestsRequestIdResolvePost({
        request_id: actionRequest.id,
        body: {
          approved: true,
          denial_reason: null,
        },
      })
      .subscribe({
        next: (resolvedRequest) => {
          console.log('Action request approved:', resolvedRequest);
          // Remove from pending list
          this.pendingActionRequests = this.pendingActionRequests.filter(
            req => req.id !== actionRequest.id
          );
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error approving action request:', err);
        },
      });
  }

  denyActionRequest(actionRequest: ActionRequestRead, reason: string = 'Denied by user') {
    this.juliaActionRequestsService
      .resolveActionRequestApiV1ActionRequestsRequestIdResolvePost({
        request_id: actionRequest.id,
        body: {
          approved: false,
          denial_reason: reason,
        },
      })
      .subscribe({
        next: (resolvedRequest) => {
          console.log('Action request denied:', resolvedRequest);
          // Remove from pending list
          this.pendingActionRequests = this.pendingActionRequests.filter(
            req => req.id !== actionRequest.id
          );
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error denying action request:', err);
        },
      });
  }

  openPreviewModal(actionRequest: ActionRequestRead) {
    console.log('Opening preview modal with action request:', actionRequest);
    this.selectedActionRequestForPreview = actionRequest;
    this.showPreviewModal = true;
  }

  closePreviewModal() {
    this.showPreviewModal = false;
    this.selectedActionRequestForPreview = null;
  }

  onModalAction(result: { action: string }) {
    if (this.selectedActionRequestForPreview) {
      if (result.action === 'approve') {
        this.approveActionRequest(this.selectedActionRequestForPreview);
      } else if (result.action === 'deny') {
        this.denyActionRequest(this.selectedActionRequestForPreview);
      }
    }
    this.closePreviewModal();
  }

}
